/**
 * Pre-deploy checklist data. Mirrors /app/memory/PRE_DEPLOY_CHECKLIST.md but
 * typed for the React UI. Update PRE_DEPLOY_CHECKLIST.md AND this file
 * together so the source-of-truth markdown and the live admin page stay in
 * sync.
 *
 * Each item has a stable `id` so localStorage tick state survives copy
 * edits to the `label`. Items also carry an optional `note` for
 * multi-line implementation detail / context that doesn't fit in a
 * single ticky line.
 *
 * Severity legend (drives the section pill colour):
 *   - 'critical'  → must do before flipping live (red)
 *   - 'important' → should do before launch (amber)
 *   - 'nice'      → can do post-launch (green)
 *   - 'done'      → already shipped (muted)
 */
export const PRE_DEPLOY_CHECKLIST = [
  {
    section: 'Critical — must do before launch',
    severity: 'critical',
    blurb: 'Launch-blockers. Anything still unticked here means you can\'t go live yet.',
    groups: [
      {
        title: 'Stripe — your own account',
        items: [
          { id: 'stripe.activate', label: 'Stripe account activated (business form, bank, tax, identity verified)', defaultChecked: true },
          { id: 'stripe.keys', label: 'Live publishable + secret keys copied from Stripe Dashboard', defaultChecked: true },
          { id: 'stripe.swap-key', label: 'STRIPE_API_KEY=sk_live_… is set in production Secrets (not in repo)', defaultChecked: true },
          { id: 'stripe.webhook', label: 'Webhook configured at forgeledger.net/api/webhook/stripe with all the required event types', defaultChecked: true },
          { id: 'stripe.whsec', label: 'STRIPE_WEBHOOK_SECRET set in production, signature verification passing', defaultChecked: true },
          { id: 'stripe.e2e', label: 'Real-card end-to-end test passed: upgrade, downgrade, customer appears in Stripe', defaultChecked: true },
          { id: 'stripe.hosted-prices', label: 'Optional (skip for now — needed only if you want Stripe Tax or full Billing Portal features)' },
        ],
      },
      {
        title: 'Stripe — proper subscriptions (not one-off payments)',
        items: [
          { id: 'subs.products', label: 'Stripe Products + Prices created for Steel/Damascus/Titanium monthly + annual', defaultChecked: true },
          { id: 'subs.env-prices', label: 'STRIPE_PRICE_* env vars added to production Secrets so prices swap per environment', defaultChecked: true },
          { id: 'subs.checkout-mode', label: 'Checkout uses subscription mode with line_items (not one-time charges)', defaultChecked: true },
          { id: 'subs.webhooks', label: 'Subscription events handled: created / updated / deleted / payment_failed', defaultChecked: true },
          { id: 'subs.downgrade', label: 'Downgrades use cancel_at_period_end so customers keep what they paid for until the cycle ends', defaultChecked: true },
          { id: 'subs.portal', label: '"Manage subscription" button in Account Settings → Billing opens the Stripe Customer Portal', defaultChecked: true },
          { id: 'subs.tests', label: 'Backend tests for subscription flow written', defaultChecked: true },
          { id: 'subs.migration', label: 'Any test/double-charges from earlier development refunded' },
          { id: 'subs.titanium-flip', label: 'LAUNCH DAY only: flip titanium_live ON at /admin/feature-flags to open Titanium checkout (rest is automatic)' },
        ],
      },
      {
        title: 'Resend (email delivery)',
        items: [
          { id: 'resend.dns', label: 'forgeledger.net domain is green in Resend (SPF, DKIM, DMARC all verified)', defaultChecked: true },
          { id: 'resend.sandbox', label: 'Out of Resend sandbox mode — can send to any address, not just verified ones', defaultChecked: true },
          { id: 'resend.deliverability', label: 'Sent test emails to Gmail / Outlook / iCloud via /admin/email-test → all landed in inbox, not spam' },
        ],
      },
      {
        title: 'Email addresses on production',
        items: [
          { id: 'env.owner-email', label: 'OWNER_EMAIL points to your real working inbox (you\'re seeing the owner pill, so this is set)', defaultChecked: true },
          { id: 'env.staff-emails', label: 'STAFF_EMAILS lists anyone else who should have staff-level access (if just you, this can be blank)' },
          { id: 'env.vat-emails', label: 'VAT_ALERT_EMAILS points to whichever inbox should get the £85k VAT-threshold warnings' },
        ],
      },
      {
        title: 'Database & backups',
        items: [
          { id: 'db.atlas', label: 'Production uses managed MongoDB (Emergent provisioned, not the preview container)', defaultChecked: true },
          { id: 'db.mongo-url', label: 'MONGO_URL set in production Secrets', defaultChecked: true },
          { id: 'db.indexes', label: 'MongoDB indexes auto-created on backend startup', defaultChecked: true },
          { id: 'db.backups', label: 'Backup + restore drill verified (see /app/memory/BACKUP_RUNBOOK.md)', defaultChecked: true },
          { id: 'db.support-questions', label: 'Asked Emergent Support the 5 backup questions (cadence / retention / restore process / RTO / PITR) — answers logged in BACKUP_RUNBOOK.md §3' },
          { id: 'db.day-zero-backup', label: 'Taken a manual mongodump of production today and stored it somewhere off-Emergent (Google Drive / encrypted Dropbox)' },
        ],
      },
      {
        title: 'Domain & SSL',
        items: [
          { id: 'domain.dns', label: 'forgeledger.net DNS pointing at Emergent', defaultChecked: true },
          { id: 'domain.ssl', label: 'TLS/SSL certificate issued and valid', defaultChecked: true },
          { id: 'domain.frontend-url', label: 'FRONTEND_PUBLIC_URL=https://forgeledger.net is set in production so password-reset links work', defaultChecked: true },
        ],
      },
      {
        title: 'Legal & compliance (UK)',
        items: [
          { id: 'legal.terms', label: 'Terms & Conditions section on /about reviewed and reflects Stripe subscriptions + Resend processor', defaultChecked: true },
          { id: 'legal.privacy', label: '"Your data, kept safe" section on /about covers GDPR/data handling', defaultChecked: true },
          { id: 'legal.cookies', label: 'Cookie banner shows on first visit (Accept all / Essential only) with revisit option in About → Cookie preferences', defaultChecked: true },
          { id: 'legal.vat', label: 'Pricing page displays the VAT statement ("Prices shown in GBP. VAT not applicable.")', defaultChecked: true },
        ],
      },
    ],
  },
  {
    section: 'Important — should do before launch',
    severity: 'important',
    blurb: 'Strongly recommended before opening to real users. Skipping these means flying blind.',
    groups: [
      {
        title: 'Error monitoring (Sentry)',
        items: [
          { id: 'sentry.backend', label: 'Sentry catches backend errors on production (test event verified)', defaultChecked: true },
          { id: 'sentry.frontend', label: 'Sentry catches frontend errors on production (test event verified)', defaultChecked: true },
          { id: 'sentry.alerts', label: 'Alert rules set up in Sentry UI (new issue / regression / error rate spike → email)', defaultChecked: true },
        ],
      },
      {
        title: 'Rate limiting',
        items: [
          { id: 'rl.tighten', label: 'Per-endpoint limits reviewed — /login, /forgot-password, /register, /contact should be strict' },
          { id: 'rl.redis', label: 'Only matters at scale: switch slowapi from in-memory to Redis-backed once running more than one backend container' },
        ],
      },
      {
        title: 'Account Health Check (held-back feature)',
        items: [
          { id: 'flag.hc-date', label: 'Decide what date you want to release the weekly Account Health Check digest' },
          { id: 'flag.hc-toggle', label: 'On release day: flip digest.enabled to ON at /admin/feature-flags' },
          { id: 'flag.hc-roadmap', label: 'Optional: change the roadmap entry from "Internal" to "Public" so users see it on the homepage roadmap' },
        ],
      },
      {
        title: 'Uptime monitoring (Better Stack)',
        note: 'Independent insurance. If Emergent\'s own status page goes down or DNS breaks, this catches it. Plus status.forgeledger.net is a quiet trust signal for paying customers.',
        items: [
          { id: 'uptime.health-endpoint', label: '/api/health endpoint exists and returns {status:"ok", db:"connected"}', defaultChecked: true },
          { id: 'uptime.signup', label: 'Create a free Better Stack account at betterstack.com (no card needed)' },
          { id: 'uptime.monitor-frontend', label: 'Add monitor for the homepage: GET https://forgeledger.net/ → expect 200, alert after 2 failures' },
          { id: 'uptime.monitor-backend', label: 'Add monitor for the API: GET https://forgeledger.net/api/health → expect 200 with body containing "ok"' },
          { id: 'uptime.notifications', label: 'Configure email alerts (SMS later, on Better Stack\'s paid tier once revenue justifies it)' },
          { id: 'uptime.status-page', label: 'Optional: point status.forgeledger.net at Better Stack\'s public status page' },
        ],
      },
      {
        title: 'AI features (LLM key) — skip for now',
        note: 'Only matters when you build The Smith (the Titanium-tier AI Money Coach). Until then, nothing in the app uses an AI service and there is no cost.',
        items: [
          { id: 'llm.balance', label: 'Skip — only matters when you ship The Smith. Pick a key strategy then (Emergent Universal Key vs your own OpenAI/Anthropic/Gemini account).' },
        ],
      },
    ],
  },
  {
    section: 'Nice-to-have — can do post-launch',
    severity: 'nice',
    blurb: 'Useful polish but not blockers. Schedule these as backlog items after the first paying customers land.',
    groups: [
      {
        title: 'Post-launch polish',
        items: [
          { id: 'nice.stripe-tax', label: 'Set up Stripe Tax once you cross the £85k VAT threshold (VAT alert will warn you in advance)' },
          { id: 'nice.billing-portal', label: 'Wire deeper Stripe Customer Portal features (invoices, address, etc.) — requires Stripe-hosted Products' },
          { id: 'nice.sitemap', label: 'Add sitemap.xml + robots.txt for better Google indexing (and to block /admin/ from being crawled)' },
          { id: 'nice.analytics', label: 'Add Google Analytics / Plausible to see traffic + conversion (remember to gate it on cookie consent)' },
          { id: 'nice.support', label: 'External support tool (HelpScout / Intercom) — only when message volume justifies it; right now /admin/messages is enough' },
          { id: 'nice.status', label: 'Public status page (statuspage.io or instatus.com) if uptime monitoring isn\'t enough' },
          { id: 'nice.404', label: 'Branded 404 / 500 pages (currently just redirects to /)' },
          { id: 'nice.social-proof', label: 'Wire /api/waitlist/count into the Pricing page so the "X people waiting" widget is live' },
          { id: 'nice.source-maps', label: 'Upload frontend source maps to Sentry so production stack traces show real code, not minified' },
          { id: 'nice.referrals', label: 'Build the referral credit system (Stripe Promotion Codes + Customer Balance)' },
          { id: 'nice.reconcile-fuzzy', label: 'Auto-reconcile: show "possible match" suggestions when amount is within 10% (catches utility-bill drift without auto-matching it silently)' },
          { id: 'nice.reconcile-drift', label: 'Auto-reconcile: detect recurring drift — if the same payee appears 3 months running at a new higher amount, prompt to update the recurring template' },
        ],
      },
    ],
  },
  {
    section: 'Already done',
    severity: 'done',
    blurb: 'Shipped pre-launch infrastructure. Don\'t delete completed items — strike them through so the audit trail survives.',
    groups: [
      {
        title: 'Foundation (auth, payments, email, DB)',
        items: [
          { id: 'done.stripe-checkout', label: 'Stripe checkout integration (Checkpoints 1-5, dynamic price_data)', defaultChecked: true },
          { id: 'done.resend', label: 'Resend email integration (sender verified, branded templates)', defaultChecked: true },
          { id: 'done.auth', label: 'JWT + Google OAuth auth', defaultChecked: true },
          { id: 'done.owner-role', label: 'Owner role with env-pinned protection + db.owner_credentials vault', defaultChecked: true },
          { id: 'done.password-reset', label: 'Password reset with HIBP breach checking + post-reset confirmation email', defaultChecked: true },
          { id: 'done.brute-force', label: 'Brute-force lockout on /login (db.login_attempts with locked_until)', defaultChecked: true },
          { id: 'done.vat-monitor', label: 'VAT trigger monitoring (daily check, alerts at £85k/£88k)', defaultChecked: true },
          { id: 'done.comp', label: 'Comp accounts with expiry (Admin)', defaultChecked: true },
          { id: 'done.flags', label: 'Feature flag infrastructure (Admin can hold features back) — public_signups_enabled, titanium_live, digest', defaultChecked: true },
          { id: 'done.tier-enforce', label: 'Tier enforcement on CSV imports, forecast horizons, auto-reconcile, Account Health Check', defaultChecked: true },
          { id: 'done.indexes', label: 'MongoDB indexes auto-created on backend startup (server.py _create_indexes)', defaultChecked: true },
        ],
      },
      {
        title: 'User-facing surfaces & UX',
        items: [
          { id: 'done.security-page', label: 'Public /security transparency page + "Your data, kept safe" section on /about', defaultChecked: true },
          { id: 'done.account-settings', label: 'Unified /account/settings hub with 6 tabs (Profile / Notifications / Preferences / Security / Billing / Privacy)', defaultChecked: true },
          { id: 'done.soft-delete', label: 'Soft-delete account workflow with 30-day grace period + automated purge cron (soft_delete_purge_runner.py)', defaultChecked: true },
          { id: 'done.pwa', label: 'PWA installable: manifest.json, service-worker.js (read-only offline cache), offline.html fallback, full icon set (192/512/maskable/apple-touch/favicons)', defaultChecked: true },
          { id: 'done.seo', label: 'Per-page SEO meta tags + OpenGraph + Twitter cards via <Seo /> component (Login/Register/Security/Home/Coming-Soon)', defaultChecked: true },
          { id: 'done.page-title', label: 'Branded <PageTitle /> component — Forge Ledger shield + H1 on every major page (customer + admin)', defaultChecked: true },
          { id: 'done.coming-soon', label: 'Public Coming-Soon landing page at / for anonymous visitors (UK-warm tone matching /security)', defaultChecked: true },
          { id: 'done.hard-gate', label: 'Hard gate — public_signups_enabled flag (default OFF) blocks /register + first-time Google signup pre-launch', defaultChecked: true },
          { id: 'done.waitlist-capture', label: 'Waitlist email capture wired to /api/waitlist (source-tagged for landing-page vs Titanium founder cohort)', defaultChecked: true },
          { id: 'done.component-split', label: 'Major monolith refactor: Statement.js, Reconciliation.js, Transactions.js, AccountSettings.js all decomposed into atomic component + hook structures', defaultChecked: true },
        ],
      },
      {
        title: 'Admin tools & operational',
        items: [
          { id: 'done.admin-nav', label: 'Admin dropdown in TopNav + MobileMenu (staff/admin/owner only) — Metrics, Releases, Feature Flags, Tiers, Moderation, Edit Roadmap/Home Copy, VAT, QA, Pre-deploy, Waitlist, Email test', defaultChecked: true },
          { id: 'done.releases', label: 'One-button release pipeline (routers/releases.py) — atomic flag flip + email blast + homepage banner. Launch-day draft "Forge Ledger is live." pre-seeded.', defaultChecked: true },
          { id: 'done.release-banner', label: '<ReleaseBanner /> on /home with per-user dismissal (db.release_dismissals)', defaultChecked: true },
          { id: 'done.waitlist-kpi', label: 'Waitlist KPI on /admin/metrics with launch-page vs Titanium-cohort breakdown — clickable into full waitlist with delete + CSV export', defaultChecked: true },
          { id: 'done.metrics', label: '/admin/metrics dashboard (MRR/ARR, paying users, conversion 30d, comped, downgrades, weekly signups, exclude-test-data toggle that filters by role + email pattern)', defaultChecked: true },
          { id: 'done.cms', label: 'CMS infrastructure with page history + restore (db.page_content_history) — /admin/edit/{help,about,pricing} + /admin/site-settings + UK English spell-check', defaultChecked: true },
          { id: 'done.digest-preview', label: 'Digest preview email infrastructure (POST /api/digest/preview + /api/digest/monthly-preview) — held behind digest feature flag', defaultChecked: true },
          { id: 'done.schedulers', label: 'Background async schedulers: digest_scheduler.py (weekly/monthly health-check emails) + soft_delete_purge_runner.py (30-day deletion)', defaultChecked: true },
          { id: 'done.messaging', label: 'Two-way threaded internal messaging — Contact form + Admin Inbox (/admin/messages)', defaultChecked: true },
          { id: 'done.email-test', label: 'Email deliverability QA tool at /admin/email-test for Gmail/Outlook/iCloud verification', defaultChecked: true },
          { id: 'done.checklists', label: 'Interactive /admin/qa-checklist + /admin/pre-deploy-checklist widgets (this page!) with localStorage tick persistence', defaultChecked: true },
          { id: 'done.deployment-health', label: 'Deployment health-check PASS (env vars in .env, CORS allowlist for forgeledger.net, supervisor config valid, no hardcoded secrets, .gitignore not blocking .env)', defaultChecked: true },
        ],
      },
    ],
  },
];
